import { FC, useMemo, useState, useCallback } from 'react';
import { Checkbox } from 'antd';
import {
  DownOutlined,
  UpOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';
import './Transfer.less';
import { $fm } from '@/Intl';

interface TransferItem {
  key: string;
  title: string | React.ReactNode;
}

interface TransferProps extends CustomFieldsProps {
  dataSource: TransferItem[];
  title: string | React.ReactNode;
  explain: string | React.ReactNode;
  limit: number;
}

const getSelectedAndAvailable = (
  value: string,
  source: TransferItem[],
): [TransferItem[], TransferItem[]] => {
  let selected: TransferItem[] = [];
  let available: TransferItem[] = [];
  let tmp = value.split(',');
  let _source = source.slice();
  tmp.forEach((v) => {
    let x = _source.findIndex((t) => t.key === v);
    if (x > -1) {
      let item = _source.splice(x, 1)[0];
      selected.push(item);
    }
  });
  available = _source;

  return [selected, available];
};

const Transfer: FC<TransferProps> = (props) => {
  const { value = '', onChange, dataSource, title, explain, limit = 4 } = props;
  const [avail, setAvail] = useState([] as TransferItem[]);
  const [seled, setSeled] = useState([] as TransferItem[]);

  const [selected, available] = useMemo(() => {
    return getSelectedAndAvailable(value, dataSource);
  }, [value, dataSource]);

  const [upActive, downActive, leftActive, rightActive] = [
    seled.length === 1 && seled[0].key !== selected[0].key,
    seled.length === 1 && seled[0].key !== selected[selected.length - 1].key,
    seled.length,
    avail.length,
  ];

  // 可选项toggle
  const toogleAvail = useCallback(
    (checked: boolean, item: TransferItem) => {
      if (checked) {
        setAvail([...avail, item]);
      } else {
        let i = avail.findIndex((v) => v.key === item.key);
        if (i > -1) {
          let tmp = avail.slice();
          tmp.splice(i, 1);
          setAvail(tmp);
        }
      }
    },
    [avail],
  );

  // 已选项toogle
  const toogleSeled = useCallback(
    (checked: boolean, item: TransferItem) => {
      if (checked) {
        setSeled([...seled, item]);
      } else {
        let i = seled.findIndex((v) => v.key === item.key);
        if (i > -1) {
          let tmp = seled.slice();
          tmp.splice(i, 1);
          setSeled(tmp);
        }
      }
    },
    [seled],
  );

  // moveLeft
  const moveLeft = useCallback(() => {
    if (!leftActive) return false;
    let _selected = selected.slice();
    seled.forEach(({ key }) => {
      let i = _selected.findIndex((item) => item.key === key);
      i > -1 && _selected.splice(i, 1);
    });
    let _value = _selected.map((item) => item.key).join(',');
    onChange && onChange(_value);
    setSeled([]);
  }, [seled, selected, onChange, leftActive]);

  // moveRight
  const moveRight = useCallback(() => {
    if (!rightActive) return false;
    let _selected = [...selected, ...avail];
    let _value = _selected.map((item) => item.key).join(',');
    onChange && onChange(_value);
    setAvail([]);
  }, [avail, selected, onChange, rightActive]);

  // moveUp
  const moveUp = useCallback(() => {
    if (!upActive) return false;
    let _selected = selected.slice();
    let curIndex = _selected.findIndex((item) => item.key === seled[0].key);
    [_selected[curIndex], _selected[curIndex - 1]] = [
      _selected[curIndex - 1],
      _selected[curIndex],
    ];
    let _value = _selected.map((item) => item.key).join(',');
    onChange && onChange(_value);
  }, [upActive, seled, selected, onChange]);

  // moveDown
  const moveDown = useCallback(() => {
    if (!downActive) return false;
    let _selected = selected.slice();
    let curIndex = _selected.findIndex((item) => item.key === seled[0].key);
    [_selected[curIndex], _selected[curIndex + 1]] = [
      _selected[curIndex + 1],
      _selected[curIndex],
    ];
    let _value = _selected.map((item) => item.key).join(',');
    onChange && onChange(_value);
  }, [downActive, seled, selected, onChange]);

  return (
    <div className='transfer-item'>
      <div className='transfer-title'>
        <h4>{title}</h4>
        <em>{explain}</em>
      </div>
      <div className='transfer-main'>
        <div className='transfer-available'>
          <h5>{$fm('SOURCE_TITLE')}</h5>
          <div className='transfer-list'>
            <div className='transfer-list-header'>
              <span>{$fm('AVAILABLE') /* 可选 */}</span>
              <strong>
                {available.length} {$fm('ITEM')}
              </strong>
            </div>
            <ul className='transfer-list-body'>
              {available.map((data) => {
                const ischecked = avail.some((a) => a.key === data.key);
                const isdisabled =
                  avail.length + selected.length >= limit && !ischecked;
                return (
                  <li key={data.key}>
                    <Checkbox
                      onChange={(e) => toogleAvail(e.target.checked, data)}
                      value={data.key}
                      checked={ischecked}
                      disabled={isdisabled}
                    >
                      {data.title}
                    </Checkbox>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
        <div className='transfer-btns'>
          <i className={rightActive ? 'active' : ''} onClick={moveRight}>
            <RightOutlined />
          </i>
          <br />
          <i className={leftActive ? 'active' : ''} onClick={moveLeft}>
            <LeftOutlined />
          </i>
          <br />
          <i className={upActive ? 'active' : ''} onClick={moveUp}>
            <UpOutlined />
          </i>
          <br />
          <i className={downActive ? 'active' : ''} onClick={moveDown}>
            <DownOutlined />
          </i>
        </div>
        <div className='transfer-selected'>
          <h5>{$fm('TARGET_TITLE') /** 显示的菜单 */}</h5>
          <div className='transfer-list'>
            <div className='transfer-list-header'>
              <span>{$fm('SELECTED') /** 已选 */}</span>
              <strong>
                {selected.length} {$fm('ITEM')}
              </strong>
            </div>
            <ul className='transfer-list-body'>
              {selected.map((data) => (
                <li key={data.key}>
                  <Checkbox
                    onChange={(e) => toogleSeled(e.target.checked, data)}
                    value={data.key}
                    checked={seled.some((s) => s.key === data.key)}
                  >
                    {data.title}
                  </Checkbox>
                </li>
              ))}
            </ul>
            {selected.length < limit ? (
              <p className='warning'>{$fm('CALLSET_WARNING', { n: limit })}</p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Transfer;
